/**
 * Gerichte: Schritte, Mengen, Umrechnen, Ordnen.
 *
 * Reine Funktionen — die App benutzt sie für Anzeige und Kochmodus, der Server
 * für die Prüfung beim Abgleich. Zweimal implementiert hieße: die Liste zeigt
 * eine andere Menge als der Kochmodus.
 */

import type { MealSlot } from './nutrition.js';

/** Höchstens so viele Schritte — eine Grenze gegen versehentliches Einfügen, kein Stil. */
export const RECIPE_MAX_STEPS = 50;
export const RECIPE_STEP_MAX_LENGTH = 1000;

/**
 * Schritte in Listenform — gleich, in welcher Form sie vorliegen.
 *
 * Bis E9 waren Schritte ein Freitext. Ältere Zeilen (auf dem Gerät oder in
 * einer noch nicht gesendeten Änderung) tragen ihn noch: Dann ist jede Zeile
 * ein Schritt. Leeres fällt weg; nichts übrig heißt leere Liste.
 */
export function normalizeSteps(value: unknown): string[] {
  const raw = Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : typeof value === 'string'
      ? value.split(/\r?\n/)
      : [];
  return raw.map((step) => step.trim()).filter((step) => step.length > 0);
}

/** Gramm aus „2 Scheiben" — `null`, wenn das Lebensmittel keine Portion kennt. */
export function gramsFromPortions(count: number, portionGrams: number | null): number | null {
  if (portionGrams === null || !(portionGrams > 0) || !(count >= 0)) return null;
  return Math.round(count * portionGrams * 10) / 10;
}

/**
 * Faktor zum Umrechnen: Das Rezept ist für `basePortions` geschrieben, gekocht
 * wird für `wanted`. Ohne sinnvolle Grundmenge bleibt alles, wie es ist.
 */
export function portionFactor(basePortions: number, wanted: number): number {
  if (!(basePortions > 0) || !(wanted > 0)) return 1;
  return wanted / basePortions;
}

/**
 * Menge für die Anzeige gerundet: unter 10 g auf eine Nachkommastelle (Gewürze,
 * Öl), darüber ganze Gramm. Eine Waage zeigt auch nicht 213,33 g.
 */
export function roundGrams(grams: number): number {
  return grams < 10 ? Math.round(grams * 10) / 10 : Math.round(grams);
}

export interface ListedRecipe {
  id: string;
  name: string;
  slot: MealSlot;
  isFavorite: boolean;
}

/**
 * Reihenfolge der Gerichte-Liste: Favoriten zuerst, darin das zuletzt
 * Gegessene vorn, Nie-Gegessenes danach alphabetisch.
 *
 * `lastEaten` bildet Gericht → letzter Tag im Protokoll (`YYYY-MM-DD`) ab.
 */
export function sortRecipes<T extends ListedRecipe>(
  recipes: readonly T[],
  lastEaten: ReadonlyMap<string, string>,
  locale = 'de',
): T[] {
  return [...recipes].sort((a, b) => {
    if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
    const dayA = lastEaten.get(a.id) ?? '';
    const dayB = lastEaten.get(b.id) ?? '';
    if (dayA !== dayB) return dayA < dayB ? 1 : -1;
    return a.name.localeCompare(b.name, locale);
  });
}

/**
 * Suche und Filter. Die Suche ignoriert Groß-/Kleinschreibung und Akzente
 * („creme" findet „Crème"), sonst findet man auf dem Telefon nichts wieder.
 */
export function filterRecipes<T extends ListedRecipe>(
  recipes: readonly T[],
  query: string,
  slot: MealSlot | null,
): T[] {
  const needle = fold(query.trim());
  return recipes.filter(
    (recipe) => (slot === null || recipe.slot === slot) && fold(recipe.name).includes(needle),
  );
}

function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** Letzter Tag je Gericht aus Protokollzeilen — gelöschte zählen nicht. */
export function lastEatenByRecipe(
  log: readonly { recipeId: string | null; date: string; deletedAt?: string | null }[],
): Map<string, string> {
  const last = new Map<string, string>();
  for (const entry of log) {
    if (!entry.recipeId || entry.deletedAt) continue;
    const known = last.get(entry.recipeId);
    if (!known || entry.date > known) last.set(entry.recipeId, entry.date);
  }
  return last;
}
